import React, {useEffect, useMemo, useState} from "react";
import {NavLink} from "react-router-dom";
import {css} from "@emotion/css";
import {styled, theme, Text, Icon} from "@reside/ui";

import {MenuItem, MenuItemContainer} from "../../../atoms/menu";
import {
  menuSectionPath,
  menuSectionPathWithDateRange,
} from "../reporting-paths";
import {
  CHECKBOXES_URL_COMPONENT,
  getReportingSectionMeta,
  MenuItem as ReportingMenuItem,
  ReportingListCategory,
} from "./helpers";
import {resolveWorkflowType} from "../../../atoms/start-workflow/workflowMapping";
import {ChecklistItem} from "../../../services/ReportingService";
import {
  isReportingSectionActive,
  resolveActiveCheckboxSectionId,
} from "../reportingBreadcrumb.helpers";

export const LOADING_PLACEHOLDER = "loading";

type DateRange = Readonly<{from?: string; to?: string}>;

type Props = Readonly<{
  /** Workflow root nodes (Admission, Arbitration, …) with section children. */
  workflows: ReadonlyArray<ReportingMenuItem>;
  checklistItems?: ReadonlyArray<ChecklistItem>;
  categoryUrl: string;
  baseDateRange: DateRange;
  pathname: string;
  /** Wraps each node so permission gating stays in `ReportingMenu`. */
  renderGate: (
    menuItem: ReportingMenuItem,
    children: React.ReactNode,
  ) => React.ReactNode;
}>;

/**
 * Renders the workflow-grouped reporting menu as collapsible accordions. Uses
 * the shared `Icon` chevron + `MenuItem` styles from `@reside/ui` rather than
 * bespoke controls, so the look matches the rest of the admin navigation.
 */
export const ReportingWorkflowGroups = ({
  workflows,
  checklistItems = [],
  categoryUrl,
  baseDateRange,
  pathname,
  renderGate,
}: Props) => {
  const activeWorkflowKey = useMemo(
    () => findActiveWorkflowKey(pathname, checklistItems),
    [pathname, checklistItems],
  );
  const activeCheckboxSectionId = useMemo(
    () => resolveActiveCheckboxSectionId(pathname),
    [pathname],
  );
  const isCheckboxesRoute = pathname.includes(`/${CHECKBOXES_URL_COMPONENT}/`);

  const defaultOpenKey = activeWorkflowKey ?? workflows[0]?.urlComponent;
  const [openWorkflows, setOpenWorkflows] = useState<Set<string>>(
    () => new Set(defaultOpenKey ? [defaultOpenKey] : []),
  );
  const [openCheckboxGroups, setOpenCheckboxGroups] = useState<Set<string>>(
    () =>
      isCheckboxesRoute && activeWorkflowKey
        ? new Set([activeWorkflowKey])
        : new Set(),
  );

  useEffect(() => {
    if (activeWorkflowKey) {
      setOpenWorkflows(prev =>
        prev.has(activeWorkflowKey)
          ? prev
          : new Set(prev).add(activeWorkflowKey),
      );
    }

    if (isCheckboxesRoute && activeWorkflowKey) {
      setOpenCheckboxGroups(prev =>
        prev.has(activeWorkflowKey)
          ? prev
          : new Set(prev).add(activeWorkflowKey),
      );
    }
  }, [activeWorkflowKey, isCheckboxesRoute]);

  const toggleWorkflow = (workflowKey: string) =>
    setOpenWorkflows(prev => {
      const next = new Set(prev);
      next.has(workflowKey) ? next.delete(workflowKey) : next.add(workflowKey);
      return next;
    });

  const renderSectionLink = (
    section: ReportingMenuItem,
    listCategory: ReportingListCategory,
    dense?: boolean,
  ) =>
    renderGate(
      section,
      <SectionLink
        key={section.urlComponent}
        section={section}
        categoryUrl={categoryUrl}
        listCategory={listCategory}
        baseDateRange={baseDateRange}
        pathname={pathname}
        dense={dense}
      />,
    );

  return (
    <>
      {workflows.map(workflow =>
        renderGate(
          workflow,
          <Accordion key={workflow.urlComponent}>
            <AccordionHeader
              type="button"
              aria-expanded={openWorkflows.has(workflow.urlComponent)}
              onClick={() => toggleWorkflow(workflow.urlComponent)}
            >
              <Text
                fontSize={3}
                fontWeight="bold"
                lineHeight="20px"
                color={
                  activeWorkflowKey === workflow.urlComponent
                    ? "primary"
                    : "darkBlue100"
                }
              >
                {workflow.label}
              </Text>
              <Chevron
                name={
                  openWorkflows.has(workflow.urlComponent) ? "down" : "right"
                }
                size={10}
              />
            </AccordionHeader>

            {openWorkflows.has(workflow.urlComponent) && (
              <AccordionBody>
                {(workflow.children ?? []).map(child => {
                  if (child.urlComponent === CHECKBOXES_URL_COMPONENT) {
                    const isCheckboxGroupActive =
                      activeCheckboxSectionId != null &&
                      (child.children ?? []).some(
                        section =>
                          section.urlComponent === activeCheckboxSectionId,
                      );
                    const isCheckboxGroupOpen =
                      openCheckboxGroups.has(workflow.urlComponent) ||
                      isCheckboxGroupActive;

                    return renderGate(
                      child,
                      <Accordion key={CHECKBOXES_URL_COMPONENT} $nested>
                        <AccordionHeader
                          type="button"
                          $nested
                          aria-expanded={isCheckboxGroupOpen}
                          onClick={() =>
                            setOpenCheckboxGroups(prev => {
                              const next = new Set(prev);
                              next.has(workflow.urlComponent)
                                ? next.delete(workflow.urlComponent)
                                : next.add(workflow.urlComponent);
                              return next;
                            })
                          }
                        >
                          <Text
                            fontSize={2}
                            fontWeight="bold"
                            lineHeight="18px"
                            color={
                              isCheckboxGroupActive ? "primary" : "darkBlue100"
                            }
                          >
                            {child.label}
                          </Text>
                          <Chevron
                            name={isCheckboxGroupOpen ? "down" : "right"}
                            size={10}
                          />
                        </AccordionHeader>

                        {isCheckboxGroupOpen && (
                          <CheckboxList>
                            {(child.children ?? []).map(section =>
                              renderSectionLink(
                                section,
                                CHECKBOXES_URL_COMPONENT,
                                true,
                              ),
                            )}
                          </CheckboxList>
                        )}
                      </Accordion>,
                    );
                  }

                  return renderSectionLink(
                    child,
                    child.listCategory ?? "general",
                  );
                })}
              </AccordionBody>
            )}
          </Accordion>,
        ),
      )}
    </>
  );
};

type SectionLinkProps = Readonly<{
  section: ReportingMenuItem;
  categoryUrl: string;
  listCategory: ReportingListCategory;
  baseDateRange: DateRange;
  pathname: string;
  /** Tighter spacing + extra indent for the nested Checkboxes list. */
  dense?: boolean;
}>;

const SectionLink = ({
  section,
  categoryUrl,
  listCategory,
  baseDateRange,
  pathname,
  dense,
}: SectionLinkProps) => {
  const Container = dense ? CheckboxItemContainer : undefined;

  if (section.urlComponent === LOADING_PLACEHOLDER) {
    return (
      <MenuItemEllipsis $indented={dense} Container={Container}>
        {section.label}
      </MenuItemEllipsis>
    );
  }

  const useDatedPath =
    categoryUrl === "reporting" && baseDateRange.from && baseDateRange.to;

  const to = useDatedPath
    ? menuSectionPathWithDateRange(
        categoryUrl,
        listCategory,
        section.urlComponent,
        baseDateRange.from,
        baseDateRange.to,
      )
    : menuSectionPath(categoryUrl, listCategory, section.urlComponent);

  const isActive = isReportingSectionActive(
    pathname,
    listCategory,
    section.urlComponent,
  );

  return (
    <MenuItemEllipsis
      $indented={dense}
      active={isActive}
      Container={Container}
    >
      <NavLink
        className={css`
          color: inherit;
          text-decoration: none;
          ${isActive && `color: ${theme.color.primary};`}
        `}
        to={to}
        title={section.label}
      >
        {section.label}
      </NavLink>
    </MenuItemEllipsis>
  );
};

/**
 * Matches the `(general|other|checkboxes)/<sectionId>` segment anywhere in the
 * path so it also resolves full-report routes that append a chart id or date
 * range after the section id.
 */
const LIST_SEGMENT_PATTERN = /\/(general|other|checkboxes)\/([^/]+)/;

/**
 * Resolve the workflow of the currently viewed report from the same section
 * metadata the breadcrumb uses, so highlight and breadcrumb never disagree.
 * Returns `undefined` when no report is selected (landing route).
 */
const findActiveWorkflowKey = (
  pathname: string,
  checklistItems: ReadonlyArray<ChecklistItem> = [],
): string | undefined => {
  const match = LIST_SEGMENT_PATTERN.exec(pathname);

  if (!match) {
    return undefined;
  }

  const meta = getReportingSectionMeta(
    match[2],
    match[1] as ReportingListCategory,
    checklistItems,
  );

  return meta ? resolveWorkflowType(meta.workflowType) : undefined;
};

const Accordion = styled.div<{$nested?: boolean}>`
  & + & {
    margin-top: 6px;
  }

  ${({$nested}) => $nested && "margin-top: 8px;"}
`;

const AccordionHeader = styled.button<{$nested?: boolean}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 6px 0 6px ${({$nested}) => ($nested ? "15px" : "0")};
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;

  &:hover {
    opacity: 0.7;
  }
`;

const AccordionBody = styled.div`
  margin-top: 2px;
`;

/** Denser list for checkbox items so long checklist labels stay compact. */
const CheckboxList = styled.div`
  margin-top: 0px;
`;

const CheckboxItemContainer = styled(MenuItemContainer)`
  margin-top: 0px;
  padding-left: 30px;
`;

const Chevron = styled(Icon)`
  flex-shrink: 0;
  color: ${({theme}) => theme.color.gray100};
`;

export const MenuItemEllipsis = styled(MenuItem)<{$indented?: boolean}>`
  ${({$indented}) => $indented && "padding-left: 15px;"}

  a {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    max-height: calc((1ex + 0.5rem) * 2);
    line-height: calc(1ex + 0.5rem);
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
  }
`;
